/**
 * Browser detection utility using Bowser
 * Detects outdated browsers and devices that we don't want to support
 */
import Bowser from 'bowser';

/**
 * Minimum browser versions we want to support
 * Any browser older than these versions will be considered outdated
 */
const MINIMUM_BROWSER_VERSIONS = {
  chrome: '60',
  firefox: '60',
  safari: '11',
  edge: '80',
  opera: '60',
  samsung: '10',
  ucbrowser: '12',
  android: '7',
  ios: '12',
};

/**
 * Checks if the current browser is too old for our site
 * @returns {boolean} True if the browser is considered outdated
 */
export const isOutdatedBrowser = () => {
  // Only run in browser environment
  if (typeof window === 'undefined' || !window.navigator || !window.navigator.userAgent) return false;
  

  try {
    // Parse the user agent
    const browser = Bowser.getParser(window.navigator.userAgent);
    
    // Check if the browser is IE (all versions are outdated, regardless of version)
    if (browser.getBrowserName().toLowerCase() === 'internet explorer') {
      return true;
    }
    
    // Check if the browser meets our minimum requirements
    const isSupported = browser.satisfies({
      // Browsers
      chrome: `>=${MINIMUM_BROWSER_VERSIONS.chrome}`,
      chromium: `>=${MINIMUM_BROWSER_VERSIONS.chrome}`,
      firefox: `>=${MINIMUM_BROWSER_VERSIONS.firefox}`,
      safari: `>=${MINIMUM_BROWSER_VERSIONS.safari}`,
      'microsoft edge': `>=${MINIMUM_BROWSER_VERSIONS.edge}`,
      opera: `>=${MINIMUM_BROWSER_VERSIONS.opera}`,
      samsung: `>=${MINIMUM_BROWSER_VERSIONS.samsung}`,
      ucbrowser: `>=${MINIMUM_BROWSER_VERSIONS.ucbrowser}`,
      
      // Operating Systems
      android: `>=${MINIMUM_BROWSER_VERSIONS.android}`,
      ios: `>=${MINIMUM_BROWSER_VERSIONS.ios}`,
    });
    
    // Also check for essential modern JavaScript features
    const missingEssentialFeatures = !(
      typeof Promise !== 'undefined' &&
      typeof Symbol !== 'undefined' &&
      typeof Map !== 'undefined' &&
      typeof Set !== 'undefined' &&
      typeof Object.assign !== 'undefined' &&
      typeof Array.prototype.includes !== 'undefined'
    );
    
    // Return true if the browser is outdated (not supported or missing features)
    return !isSupported || missingEssentialFeatures;
  } catch (error) {
    // If there's an error in detection, log it and assume the browser is supported
    console.error('Error detecting browser:', error);
    return false;
  }
};

// isTestEnvironment function has been removed as it's no longer needed
